import {
  Modal,
  SpaceBetween,
  FileUpload,
  FormField,
  Input,
  Textarea,
  Button,
  TokenGroup,
  Alert
} from "@cloudscape-design/components";
import { useState } from "react";
import { UploadExtensionModalProps } from "types/extensionTypes";





function UploadExtensionModal({
  visible,
  isLoadingUpload,
  onSubmit,
  onDismiss
}: UploadExtensionModalProps) {
    
    const [files, setFiles] = useState<File[]>([]);

    const fileSelected = files.length > 0;


    const [versionInput, setVersionInput] = useState("");
    const [supportedVersions, setSupportedVersions] = useState<string[]>([]);

    const [extensionName, setExtensionName] = useState<string>("");
    const [extensionDescription, setExtensionDescription] = useState<string>("");

    const [error, setError] = useState<string | null>(null);


    const isFormValid = () => {
        return (
            files.length > 0 &&
            extensionName.trim() !== "" &&
            extensionDescription.trim() !== "" &&
            supportedVersions.length > 0
        );
    };


    const resetForm = ()=>{
        setFiles([]);
        setVersionInput("");
        setSupportedVersions([]);
        setExtensionName("");
        setExtensionDescription("");
    }


    const handleOnDissmiss = ()=>{
        if(isLoadingUpload) return;

        resetForm();
        setError(null);
        onDismiss();
    }


    const handleSubmit = async () => {
        try{
            setError(null);

            await onSubmit({
                file: files[0],
                extensionName,
                extensionDescription,
                supportedVersions
            });

            handleOnDissmiss();

        } catch (error){
            setError("Failed to upload the extension. Please try again.");
        }
    };

    
    if(!visible) return null;



  return (
    <Modal
      visible={visible}
      onDismiss={handleOnDissmiss}
      size="large"
      header="Upload Custom Extension"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={handleOnDissmiss} disabled={isLoadingUpload}>
            Cancel
          </Button>

          <Button
            variant="primary"
            disabled={!isFormValid()}
            onClick={handleSubmit}
            loading={isLoadingUpload}
          >
            Upload Extension
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="l">

        <FormField
          label={<>Extension JAR <span className="required-field">*</span></>}
          description="Upload your Keycloak extension (.jar)"
        >
            <FileUpload
                value={files}
                onChange={({ detail }) => setFiles(detail.value)}
                i18nStrings={{
                    uploadButtonText: count =>
                    count ? "Choose another file" : "Choose file",
                    dropzoneText: multiple =>
                    multiple
                        ? "Drag and drop files here"
                        : "Drag and drop your .jar file here",
                    removeFileAriaLabel: index =>
                    `Remove file ${index + 1}`,
                    limitShowFewer: "Show fewer files",
                    limitShowMore: "Show more files",
                    errorIconAriaLabel: "Error"
                }}
                showFileLastModified
                showFileSize
                multiple={false}
                accept=".jar"
            />
        </FormField>

        {fileSelected && (
            <>
                <FormField label={<>Extension Name <span className="required-field">*</span></>}>
                    <Input 
                        placeholder="e.g. Company Authentication Provider" 
                        value={extensionName} 
                        disabled={isLoadingUpload}
                        onChange={({detail})=>{setExtensionName(detail.value)}}/>
                </FormField>

                <FormField label={<>Description <span className="required-field">*</span></>}>
                    <Textarea
                        rows={3}
                        disabled={isLoadingUpload}
                        placeholder="Describe what this extension does..." 
                        value={extensionDescription}
                        onChange={({detail})=>{setExtensionDescription(detail.value)}}
                    />
                </FormField>

                <FormField
                    label={<>Supported Keycloak Versions <span className="required-field">*</span></>}
                    description="Add one version at a time."
                >
                    <SpaceBetween size="xs">

                        <SpaceBetween direction="horizontal" size="xs">
                            <Input
                                value={versionInput}
                                placeholder="26.5.4"
                                disabled={isLoadingUpload}
                                onChange={({ detail }) => setVersionInput(detail.value)}
                            />

                            <Button
                                disabled={isLoadingUpload}
                                onClick={() => {
                                    const version = versionInput.trim();

                                    if (
                                        version &&
                                        !supportedVersions.includes(version)
                                    ) {
                                        setSupportedVersions([
                                            ...supportedVersions,
                                            version
                                        ]);
                                        setVersionInput("");
                                    }
                                }}
                            >
                                Add
                            </Button>
                        </SpaceBetween>

                        <TokenGroup
                            items={supportedVersions.map(version => ({
                                label: version
                            }))}
                            onDismiss={({ detail }) => {
                                if(isLoadingUpload) return;
                                
                                setSupportedVersions(current =>
                                    current.filter((_, index) => index !== detail.itemIndex)
                                );
                            }}
                        />

                    </SpaceBetween>
                </FormField>
            </>
        )}

        {error && <Alert type="error">
            {error}
        </Alert>}

      </SpaceBetween>
    </Modal>
  );
}

export default UploadExtensionModal;